// External modules
import { Category }      from '../models/category';
import { Contact }       from '../models/contact';
import { KeyPoint }      from '../models/keypoint';
import { Language }      from '../models/language';
import { Mixture }       from '../models/mixture';
import { Product }       from '../models/product';
import { ProductFamily } from '../models/productFamily';
import { Cart }          from '../../shared/providers/cart.provider';
import { ArrayTyper }    from '@caliatys/array-typer';

// Interfaces
import { Document }      from '../interfaces/document.interface';
import { User }          from '../interfaces/user.interface';

// Enums
import { StorageKey }    from '../enums/storage-key.enum';

export class StorageHelper
{
  // ----------------------------------------------------------------------------------------------
  // SECTION User -------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setUser(value: User): void
  {
    const key = StorageKey.USER;
    StorageHelper.setItem(key, value);
  }

  public static getUser(): User|null
  {
    const key = StorageKey.USER;
    return StorageHelper.getItem(key);
  }

  public static removeUser(): void
  {
    const key = StorageKey.USER;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION Document ----------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setDocument(value: Document): void
  {
    const key = StorageKey.DOCUMENT;
    StorageHelper.setItem(key, value);
  }

  public static getDocument(): Document|null
  {
    const key = StorageKey.DOCUMENT;
    return StorageHelper.getItem(key);
  }

  public static removeDocument(): void
  {
    const key = StorageKey.DOCUMENT;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION KeyPoints ---------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setKeyPoints(value: KeyPoint[]): void
  {
    const key = StorageKey.KEY_POINTS;
    StorageHelper.setItem(key, value);
  }

  public static getKeyPoints(): KeyPoint[]|null
  {
    const key = StorageKey.KEY_POINTS;
    return StorageHelper.getItem(key);
  }

  public static removeKeyPoints(): void
  {
    const key = StorageKey.KEY_POINTS;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION Mixtures ----------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setMixtures(value: Mixture[]): void
  {
    const key = StorageKey.MIXTURES;
    StorageHelper.setItem(key, value);
  }

  public static getMixtures(): Mixture[]|null
  {
    const key = StorageKey.MIXTURES;
    return StorageHelper.getItem(key);
  }

  public static removeMixtures(): void
  {
    const key = StorageKey.MIXTURES;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION Products ----------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setProducts(value: Product[]): void
  {
    const key = StorageKey.PRODUCTS;
    StorageHelper.setItem(key, value);
  }

  public static getProducts(): Product[]|null
  {
    const key = StorageKey.PRODUCTS;
    const data = StorageHelper.getItem<Product[]|null>(key);
    if(data === null)
      return null;

    return ArrayTyper.asArray(Product, data);
  }

  public static removeProducts(): void
  {
    const key = StorageKey.PRODUCTS;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION ProductFamilies ----------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setProductFamilies(value: ProductFamily[]): void
  {
    const key = StorageKey.PRODUCT_FAMILIES;
    StorageHelper.setItem(key, value);
  }

  public static getProductFamilies(): ProductFamily[]|null
  {
    const key = StorageKey.PRODUCT_FAMILIES;
    return StorageHelper.getItem(key);
  }

  public static removeProductFamilies(): void
  {
    const key = StorageKey.PRODUCT_FAMILIES;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION Categories --------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setCategories(value: Category[]): void
  {
    const key = StorageKey.CATEGORIES;
    StorageHelper.setItem(key, value);
  }

  public static getCategories(): Category[]|null
  {
    const key = StorageKey.CATEGORIES;
    return StorageHelper.getItem(key);
  }

  public static removeCategories(): void
  {
    const key = StorageKey.CATEGORIES;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION Selected Lang ------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setSelectedLanguage(value: string): void
  {
    const key = StorageKey.SELECTED_LANGUAGE;
    StorageHelper.setItem(key, value);
  }

  public static getSelectedLanguage(): string|null
  {
    const key = StorageKey.SELECTED_LANGUAGE;
    return StorageHelper.getItem(key);
  }

  public static removeSelectedLanguage(): void
  {
    const key = StorageKey.SELECTED_LANGUAGE;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION Languages ---------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setLanguages(value: Language[]): void
  {
    const key = StorageKey.LANGUAGES;
    StorageHelper.setItem(key, value);
  }

  public static getLanguages(): Language[]|null
  {
    const key = StorageKey.LANGUAGES;
    return StorageHelper.getItem(key);
  }

  public static removeLanguages(): void
  {
    const key = StorageKey.LANGUAGES;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION Cheat --------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setCheat(value: boolean): void
  {
    const key = StorageKey.CHEAT;
    StorageHelper.setItem(key, value);
  }

  public static getCheat(): boolean|null
  {
    const key = StorageKey.CHEAT;
    return StorageHelper.getItem(key);
  }

  public static removeCheat(): void
  {
    const key = StorageKey.CHEAT;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION Unit ---------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setUnit(value: string): void
  {
    const key = StorageKey.UNIT;
    StorageHelper.setItem(key, value);
  }

  public static getUnit(): string|null
  {
    const key = StorageKey.UNIT;
    return StorageHelper.getItem(key);
  }

  public static removeUnit(): void
  {
    const key = StorageKey.UNIT;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION Cart -------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setCart(value: any): void
  {
    const key = StorageKey.CART;
    StorageHelper.setItem(key, value);
  }

  public static getCart(): any|null
  {
    const key = StorageKey.CART;
    const cart = StorageHelper.getItem(key);
    if (cart != null)
      return new Cart(cart);

    return new Cart();
  }

  public static removeCart(): void
  {
    const key = StorageKey.CART;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION Contacts -----------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setContacts(value: Contact[]): void
  {
    const key = StorageKey.CONTACTS;
    StorageHelper.setItem(key, value);
  }

  public static getContacts(): Contact[]|null
  {
    const key = StorageKey.CONTACTS;
    return StorageHelper.getItem(key);
  }

  public static removeContacts(): void
  {
    const key = StorageKey.CONTACTS;
    StorageHelper.removeItem(key);
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // SECTION LocalStorage -------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  private static setItem(key: string, value: any) : void
  {
    localStorage.setItem(key, JSON.stringify(value));
  }

  private static getItem<T>(key: string) : T
  {
    const res = localStorage.getItem(key);
    if (res !== 'undefined')
      return JSON.parse(res as any);

    console.error('StorageHelper : getItem -> undefined key');
    return null;
  }

  private static removeItem(key: string) : void
  {
    localStorage.removeItem(key);
  }

  public static getKeys() : string[]
  {
    return Object.keys(localStorage);
  }

  public static clearItems() : void
  {
    localStorage.clear();
  }

  // ----------------------------------------------------------------------------------------------
  // !SECTION -------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
}


// WEBPACK FOOTER //
// ./src/app/shared/helpers/storage.helper.ts